import {
  type Dirent,
  existsSync,
  lstatSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  realpathSync,
  rmSync,
  statSync,
  writeFileSync,
} from 'node:fs';
import { dirname, isAbsolute, join, relative, sep } from 'node:path';
import { exec } from 'node:child_process';
import { promisify } from 'node:util';
import { z } from 'zod';
import { resolveInRepo } from './paths.js';
import type { ToolResult, ToolSpec } from './types.js';

const pexec = promisify(exec);

const MAX_OUTPUT = 8000;
const MAX_FILE_BYTES = 512 * 1024;
const EXEC_TIMEOUT_MS = 30_000;
const GREP_MAX_HITS = 100;
const GREP_BUDGET_MS = 5_000;
const GREP_MAX_LINE = 2_000;
const NUL = String.fromCharCode(0);
const WALK_IGNORE = new Set([
  '.git',
  'node_modules',
  'dist',
  'build',
  '.next',
  '.turbo',
  'coverage',
  '.data',
  '.cache',
]);

/** Destructive commands refused even after approval (defense-in-depth; the permission gate is primary). */
const EXEC_DENYLIST: readonly RegExp[] = [
  /\brm\s+(-\w*[rf]\w*|--(recursive|force|no-preserve-root))/i,
  /\b(del|erase)\s+\/[sq]/i,
  /\b(rd|rmdir)\b[^\n]*\/s/i,
  /\b(Remove-Item|ri)\b[^\n]*-Recurse/i,
  /\bfind\b[^\n]*-delete\b/i,
  /\bchmod\s+-R\b/i,
  /\bformat\b/i,
  /\bmkfs\b/i,
  /\bdd\s+if=/i,
  /\b(shutdown|reboot|halt)\b/i,
  /:\(\)\s*\{.*\};\s*:/, // fork bomb
  /\bgit\s+push\b/i,
  /\b(curl|wget|irm|iwr)\b[^|]*\|\s*(sh|bash|zsh|iex|powershell)/i,
  />\s*\/dev\/sd/i,
];

function truncate(s: string, n = MAX_OUTPUT): string {
  return s.length > n ? `${s.slice(0, n)}\n…[truncated ${s.length - n} chars]` : s;
}

/**
 * Symlink-escape guard: the lexical check confines the path STRING; verify the
 * REAL parent directory is still inside the repo (a symlinked dir could
 * otherwise escape). The parent must exist when this is called.
 */
function realParentEscapesRepo(abs: string, repoRoot: string): boolean {
  const realParent = realpathSync(dirname(abs));
  const relParent = relative(realpathSync(repoRoot), realParent);
  return relParent === '..' || relParent.startsWith(`..${sep}`) || isAbsolute(relParent);
}

function walkFiles(
  dir: string,
  repoRoot: string,
  onFile: (rel: string, content: string) => void,
  stop: () => boolean,
): void {
  let entries: Dirent[];
  try {
    entries = readdirSync(dir, { withFileTypes: true });
  } catch {
    return;
  }
  for (const entry of entries) {
    if (stop()) return;
    const abs = join(dir, entry.name);
    if (entry.isDirectory()) {
      if (!WALK_IGNORE.has(entry.name)) walkFiles(abs, repoRoot, onFile, stop);
      continue;
    }
    if (!entry.isFile()) continue;
    try {
      if (statSync(abs).size > MAX_FILE_BYTES) continue;
      const content = readFileSync(abs, 'utf8');
      if (content.slice(0, 8000).includes(NUL)) continue;
      onFile(relative(repoRoot, abs).split(sep).join('/'), content);
    } catch {
      /* unreadable — skip */
    }
  }
}

const readFileSpec: ToolSpec = {
  name: 'read_file',
  description: 'Read a UTF-8 text file from the repository.',
  parameters: {
    type: 'object',
    properties: { path: { type: 'string', description: 'repo-relative file path' } },
    required: ['path'],
    additionalProperties: false,
  },
  mutating: false,
  schema: z.object({ path: z.string().min(1) }),
  preview: (args) => `read ${(args as { path: string }).path}`,
  classify: () => null,
  async run(args, ctx): Promise<ToolResult> {
    const { path } = args as { path: string };
    const abs = resolveInRepo(ctx.repoRoot, path);
    if (!existsSync(abs)) return { ok: false, output: `not found: ${path}` };
    return { ok: true, output: truncate(readFileSync(abs, 'utf8')) };
  },
};

const listDirSpec: ToolSpec = {
  name: 'list_dir',
  description: 'List the entries of a directory in the repository.',
  parameters: {
    type: 'object',
    properties: { path: { type: 'string', description: 'repo-relative directory (default ".")' } },
    required: [],
    additionalProperties: false,
  },
  mutating: false,
  schema: z.object({ path: z.string().optional() }),
  preview: (args) => `list ${(args as { path?: string }).path ?? '.'}`,
  classify: () => null,
  async run(args, ctx): Promise<ToolResult> {
    const { path } = args as { path?: string };
    const abs = resolveInRepo(ctx.repoRoot, path && path.length > 0 ? path : '.');
    let entries: Dirent[];
    try {
      entries = readdirSync(abs, { withFileTypes: true });
    } catch {
      return { ok: false, output: `cannot list: ${path ?? '.'}` };
    }
    const names = entries.map((e) => (e.isDirectory() ? `${e.name}/` : e.name)).sort();
    return { ok: true, output: truncate(names.join('\n')) || '(empty)' };
  },
};

const grepSpec: ToolSpec = {
  name: 'grep_search',
  description:
    'Search repository files for a JavaScript regular expression. Returns matching lines as "path:line: text".',
  parameters: {
    type: 'object',
    properties: {
      pattern: { type: 'string', description: 'regular expression' },
      path: { type: 'string', description: 'repo-relative directory to search (default ".")' },
    },
    required: ['pattern'],
    additionalProperties: false,
  },
  mutating: false,
  schema: z.object({ pattern: z.string().min(1), path: z.string().optional() }),
  preview: (args) => `grep /${(args as { pattern: string }).pattern}/`,
  classify: () => null,
  async run(args, ctx): Promise<ToolResult> {
    const { pattern, path } = args as { pattern: string; path?: string };
    let re: RegExp;
    try {
      re = new RegExp(pattern);
    } catch {
      return { ok: false, output: `invalid regex: ${pattern}` };
    }
    const root = resolveInRepo(ctx.repoRoot, path && path.length > 0 ? path : '.');
    const hits: string[] = [];
    const deadline = Date.now() + GREP_BUDGET_MS;
    const stop = (): boolean => hits.length >= GREP_MAX_HITS || Date.now() > deadline;
    walkFiles(
      root,
      ctx.repoRoot,
      (rel, content) => {
        const lines = content.split('\n');
        for (let i = 0; i < lines.length && !stop(); i++) {
          const line = lines[i] ?? '';
          // Skip very long (minified) lines so a model-supplied regex can't ReDoS on them.
          if (line.length <= GREP_MAX_LINE && re.test(line)) {
            hits.push(`${rel}:${i + 1}: ${line.trim().slice(0, 200)}`);
          }
        }
      },
      stop,
    );
    const note = Date.now() > deadline ? '\n…[search time budget reached]' : '';
    return {
      ok: true,
      output: hits.length > 0 ? truncate(hits.join('\n')) + note : 'no matches',
    };
  },
};

const writeFileSpec: ToolSpec = {
  name: 'write_file',
  description: 'Create or overwrite a text file in the repository.',
  parameters: {
    type: 'object',
    properties: {
      path: { type: 'string', description: 'repo-relative file path' },
      content: { type: 'string', description: 'full new file contents' },
    },
    required: ['path', 'content'],
    additionalProperties: false,
  },
  mutating: true,
  schema: z.object({ path: z.string().min(1), content: z.string() }),
  preview: (args) => {
    const a = args as { path: string; content: string };
    return `write ${a.path} (${a.content.length} bytes)`;
  },
  // Overwriting an existing file is an EDIT; a new path is a CREATE — the two
  // carry independent consent states. Unresolvable paths classify as `create`
  // (the stricter-sounding default); run() re-validates and refuses anyway.
  classify: (args, ctx) => {
    try {
      return existsSync(resolveInRepo(ctx.repoRoot, (args as { path: string }).path))
        ? 'edit'
        : 'create';
    } catch {
      return 'create';
    }
  },
  async run(args, ctx): Promise<ToolResult> {
    const { path, content } = args as { path: string; content: string };
    const abs = resolveInRepo(ctx.repoRoot, path);
    mkdirSync(dirname(abs), { recursive: true });
    if (realParentEscapesRepo(abs, ctx.repoRoot)) {
      return { ok: false, output: 'refused: resolved path escapes the repository root' };
    }
    // writeFileSync FOLLOWS an existing symlink — refuse when its real target
    // lives outside the repo (the parent check above only covers directories).
    if (existsSync(abs)) {
      const real = realpathSync(abs);
      const rel = relative(realpathSync(ctx.repoRoot), real);
      if (rel === '..' || rel.startsWith(`..${sep}`) || isAbsolute(rel)) {
        return { ok: false, output: 'refused: resolved path escapes the repository root' };
      }
    }
    writeFileSync(abs, content);
    return { ok: true, output: `wrote ${content.length} bytes to ${path}` };
  },
};

const deleteFileSpec: ToolSpec = {
  name: 'delete_file',
  description: 'Delete a single file from the repository (not directories).',
  parameters: {
    type: 'object',
    properties: { path: { type: 'string', description: 'repo-relative file path' } },
    required: ['path'],
    additionalProperties: false,
  },
  mutating: true,
  schema: z.object({ path: z.string().min(1) }),
  preview: (args) => `delete ${(args as { path: string }).path}`,
  classify: () => 'delete',
  async run(args, ctx): Promise<ToolResult> {
    const { path } = args as { path: string };
    const abs = resolveInRepo(ctx.repoRoot, path);
    if (!existsSync(abs)) return { ok: false, output: `not found: ${path}` };
    // lstat (not stat): refuse symlinks outright rather than judging by their target.
    if (!lstatSync(abs).isFile()) {
      return { ok: false, output: `refused: ${path} is not a regular file` };
    }
    if (realParentEscapesRepo(abs, ctx.repoRoot)) {
      return { ok: false, output: 'refused: resolved path escapes the repository root' };
    }
    rmSync(abs);
    return { ok: true, output: `deleted ${path}` };
  },
};

const execSpec: ToolSpec = {
  name: 'terminal_exec',
  description: 'Run a shell command in the repository root (builds, tests, inspection). Output is captured.',
  parameters: {
    type: 'object',
    properties: { command: { type: 'string', description: 'the shell command' } },
    required: ['command'],
    additionalProperties: false,
  },
  mutating: true,
  schema: z.object({ command: z.string().min(1) }),
  preview: (args) => `run: ${(args as { command: string }).command}`,
  classify: () => 'run',
  async run(args, ctx): Promise<ToolResult> {
    const { command } = args as { command: string };
    if (EXEC_DENYLIST.some((re) => re.test(command))) {
      return { ok: false, output: 'refused: command matches the destructive denylist' };
    }
    try {
      // killSignal makes the timeout terminate hard. NOTE: this does not kill a
      // descendant process tree on Windows — a full Job-Object kill-tree is a
      // tracked hardening refinement; the permission gate is the primary boundary.
      const { stdout, stderr } = await pexec(command, {
        cwd: ctx.repoRoot,
        timeout: EXEC_TIMEOUT_MS,
        killSignal: 'SIGKILL',
        maxBuffer: 1_000_000,
        windowsHide: true,
      });
      const out = `${stdout}${stderr ? `\n[stderr]\n${stderr}` : ''}`;
      return { ok: true, output: truncate(out) || '(no output)' };
    } catch (err) {
      const e = err as { stdout?: string; stderr?: string; message?: string; killed?: boolean };
      const body =
        `${e.killed ? 'command timed out\n' : ''}${e.stdout ?? ''}${e.stderr ?? ''}` ||
        (e.message ?? 'command failed');
      return { ok: false, output: truncate(body) };
    }
  },
};

/** A lookup over the built-in tools. */
export interface ToolRegistry {
  readonly specs: readonly ToolSpec[];
  get(name: string): ToolSpec | undefined;
}

export function createToolRegistry(): ToolRegistry {
  const specs: readonly ToolSpec[] = [
    readFileSpec,
    listDirSpec,
    grepSpec,
    writeFileSpec,
    deleteFileSpec,
    execSpec,
  ];
  const byName = new Map(specs.map((s) => [s.name, s] as const));
  return { specs, get: (name) => byName.get(name) };
}
